import React from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X, Shield, ArrowLeft } from "lucide-react";

const AccessDeniedModal = ({
  isOpen,
  onClose,
  title = "Access Denied",
  message = "You don't have the necessary permissions to access this page or feature.",
  showBackButton = true,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleGoBack = () => {
    navigate(-1);
    if (onClose) onClose();
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75 z-[9999]"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative z-[10000] inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-3 items-center">
              <div className="p-3 bg-red-100 rounded-xl">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500">Permission Required</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 transition-colors hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <div className="p-4 mb-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex gap-3 items-start">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-orange-800">
                    Insufficient Permissions
                  </p>
                  <p className="mt-1 text-sm text-orange-700">{message}</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600">
                If you believe you should have access to this feature, please
                contact your administrator to request the necessary permissions.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3">
            {showBackButton && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleGoBack}
                className="flex flex-1 gap-2 items-center px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGoToDashboard}
              className="flex-1 px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700"
            >
              Go to Dashboard
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AccessDeniedModal;

